import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  CircularProgress,
  Grid,
  Paper,
  Typography,
  useTheme,
  Link,
} from "@mui/material";
import Header from "../../components/Header";
import { tokens } from "../../theme";

function ViewVideoById() {
  const { id } = useParams();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [videoDetails, setVideoDetails] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchVideoDetails();
  }, [id]);

  const fetchVideoDetails = async () => {
    setLoading(true);
    const token = localStorage.getItem("token");
    
    try {
      // Fetch video details with token
      const videoResponse = await fetch(
        `https://backend.placemyfilms.com/videosUpload/getById/${id}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      const videoResponseData = await videoResponse.json();

      if (videoResponseData.status && videoResponseData.result) {
        const videoData = videoResponseData.result;
        setVideoDetails(videoData);

        // Fetch user details using user_id from video with token
        if (videoData.user_id) {
          const userResponse = await fetch(
            `https://backend.placemyfilms.com/user/getUserById/${videoData.user_id}`,
            {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            }
          );
          const userResponseData = await userResponse.json();

          if (userResponseData.status && userResponseData.result.length > 0) {
            setUserDetails(userResponseData.result[0]);
          }
        }
      } else {
        setError(`Failed to fetch video details: ${videoResponseData.message}`);
      }
    } catch (err) {
      setError(`Error fetching details: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const formatFieldName = (field) => {
    return field.replace(/([A-Z])/g, " $1").trim();
  };

  const formatValue = (field, value) => {
    if (field === 'createdAt' || field === 'updatedAt') {
      return new Date(value).toLocaleString();
    }
    
    if (field === 'url') {
      return (
        <Link 
          href={value} 
          target="_blank" 
          rel="noopener noreferrer"
          sx={{ 
            color: colors.blueAccent[400],
            '&:hover': {
              color: colors.blueAccent[300],
            }
          }}
        >
          {value}
        </Link>
      );
    }

    return value !== null && value !== "" ? value : "Not Available";
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box m="20px">
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box m="20px" height="70vh" overflow="auto" paddingRight="20px">
      <Header title="Video Details" subtitle={`Video ID: ${id}`} />
      <Box ml={"40px"}>
        <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
          {videoDetails && (
            <>
              <Typography
                variant="h6"
                fontWeight="bold"
                mb={2}
                color={colors.blueAccent[400]}
              >
                Video Details
              </Typography>
              <Grid container spacing={2}>
                {Object.entries(videoDetails).map(([field, value]) => (
                  <Grid item xs={12} key={field}>
                    <Grid container>
                      <Grid item xs={3}>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {formatFieldName(field)}:
                        </Typography>
                      </Grid>
                      <Grid item xs={9}>
                        {typeof value === 'object' ? (
                          <Typography>
                            {JSON.stringify(value)}
                          </Typography>
                        ) : (
                          <Typography component="div">
                            {formatValue(field, value)}
                          </Typography>
                        )}
                      </Grid>
                    </Grid>
                  </Grid>
                ))}
              </Grid>
            </>
          )}

          {userDetails && (
            <>
              <Typography
                variant="h6"
                fontWeight="bold"
                mb={2}
                mt={4}
                color={colors.blueAccent[400]}
              >
                User Details
              </Typography>
              <Grid container spacing={2}>
                {Object.entries(userDetails).map(([field, value]) => (
                  field !== 'password' && field !== 'otp' && (
                    <Grid item xs={12} key={field}>
                      <Grid container>
                        <Grid item xs={3}>
                          <Typography variant="subtitle1" fontWeight="bold">
                            {formatFieldName(field)}:
                          </Typography>
                        </Grid>
                        <Grid item xs={9}>
                          <Typography component="div">
                            {formatValue(field, value)}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Grid>
                  )
                ))}
              </Grid>
            </>
          )}
        </Paper>
      </Box>
    </Box>
  );
}

export default ViewVideoById;